import React, { useMemo } from "react";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import Alert from "../../../components/common/Alert";
import Badge from "../../../components/common/Badge";
import Button from "../../../components/common/Button";
import Card from "../../../components/common/Card";
import Section from "../../../components/common/Section";
import Table from "../../../components/common/Table";
import { useGetCashingUpSessionQuery } from "../../../store/api";
import { formatDateTime } from "../../../utils/reporting";
import {
  formatAmountWithImpact,
  formatSignedCurrency,
  formatStatusLabel,
  getStatusTone,
} from "../../../utils/cashingUpReporting";

const DetailPair = ({ label, value, emphasize = false }) => (
  <div className="min-w-0 border-b border-slate-100 py-3 last:border-b-0">
    <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
      {label}
    </p>
    <div
      className={`mt-1 min-w-0 break-words text-slate-900 ${
        emphasize ? "text-base font-semibold" : "text-sm font-medium"
      }`}
    >
      {value == null || value === "" ? "-" : value}
    </div>
  </div>
);

const Metric = ({ label, value, tone = "slate" }) => {
  const toneClass = {
    emerald: "text-emerald-700",
    rose: "text-rose-700",
    slate: "text-slate-900",
  }[tone];

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50/70 px-4 py-3">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className={`mt-1 text-xl font-semibold ${toneClass}`}>{value}</p>
    </div>
  );
};

const parseJsonValue = (value, fallback) => {
  if (value == null || value === "") return fallback;
  if (typeof value === "object") return value;
  if (typeof value !== "string") return fallback;
  try {
    return JSON.parse(value);
  } catch (error) {
    return fallback;
  }
};

const formatBusinessDate = (value) => {
  if (!value) return "-";
  const [year, month, day] = String(value).split("-").map(Number);
  if (!year || !month || !day) return value;
  return new Intl.DateTimeFormat("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(new Date(year, month - 1, day));
};

const CashSessionDetail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cashSessionId } = useParams();
  const [searchParams] = useSearchParams();

  const sessionFromState = location.state?.session || null;
  const storeId =
    searchParams.get("storeId") ||
    sessionFromState?.storeId ||
    sessionFromState?.store?.storeId ||
    "";

  const queryArgs = useMemo(
    () => ({ cashSessionId, storeId }),
    [cashSessionId, storeId],
  );

  const {
    data: sessionResponse,
    isLoading,
    isFetching,
    isError,
    error,
  } = useGetCashingUpSessionQuery(queryArgs, {
    skip: !cashSessionId || !storeId,
  });

  const sessionData = useMemo(() => {
    if (!sessionResponse) return null;
    if (sessionResponse.session && typeof sessionResponse.session === "object") {
      return sessionResponse.session;
    }
    if (sessionResponse.data && typeof sessionResponse.data === "object") {
      return sessionResponse.data;
    }
    return sessionResponse;
  }, [sessionResponse]);

  const session = useMemo(
    () => (sessionData ? { ...sessionFromState, ...sessionData } : sessionFromState),
    [sessionData, sessionFromState],
  );

  const currencyCode = session?.currencyCode || "USD";
  const expectedBreakdown = session?.expectedCashBreakdown || {};
  const variance = formatAmountWithImpact(session?.cashVariance, currencyCode);
  const cashMovements = Array.isArray(session?.cashMovements)
    ? session.cashMovements
    : [];
  const tenderSummaries = Array.isArray(session?.tenderSummaries)
    ? session.tenderSummaries
    : parseJsonValue(session?.expectedTenderTotalsJson, []);
  const reconciliationLines = Array.isArray(session?.reconciliationLines)
    ? session.reconciliationLines
    : [];
  const pdqRefundValue = expectedBreakdown.cardRefunds ?? session?.cardRefunds;

  const overviewRows = [
    session?.storeName ? { label: "Store", value: session.storeName } : null,
    { label: "Till", value: session?.tillName || "-" },
    { label: "Business Date", value: formatBusinessDate(session?.businessDate) },
    {
      label: "Session Status",
      value: (
        <Badge tone={getStatusTone(session?.status)}>
          {formatStatusLabel(session?.status)}
        </Badge>
      ),
    },
    {
      label: "Sync Status",
      value: (
        <Badge tone={getStatusTone(session?.syncStatus)}>
          {formatStatusLabel(session?.syncStatus)}
        </Badge>
      ),
    },
    { label: "Opened At", value: formatDateTime(session?.openedAt) },
    session?.closedAt
      ? { label: "Closed At", value: formatDateTime(session.closedAt) }
      : null,
    session?.countSubmittedAt
      ? { label: "Count Submitted", value: formatDateTime(session.countSubmittedAt) }
      : null,
    session?.lastSyncedAt
      ? { label: "Last Synced", value: formatDateTime(session.lastSyncedAt) }
      : null,
    session?.localReferenceNo
      ? { label: "Reference", value: session.localReferenceNo }
      : null,
  ].filter(Boolean);

  const breakdownRows = [
    ["Opening Cash", expectedBreakdown.openingCash ?? session?.openingCash],
    ["Cash Payments", expectedBreakdown.cashPayments],
    ["Cash Refunds", expectedBreakdown.cashRefunds],
    ["PDQ Refunds", pdqRefundValue],
    ["Paid Out", expectedBreakdown.paidOut],
    ["Cash Drops", expectedBreakdown.cashDrop],
    ["Tip Payouts", expectedBreakdown.tipPayout],
  ].filter(([, value]) => value != null);

  const movementRows = cashMovements.map((movement, index) => ({
    id: movement.cashMovementId || index,
    type: formatStatusLabel(movement.movementType),
    amount: formatSignedCurrency(movement.amount, currencyCode),
    reason: movement.reasonCode ? formatStatusLabel(movement.reasonCode) : "-",
    createdAt: formatDateTime(movement.createdAt),
  }));

  const tenderRows = tenderSummaries.map((tender, index) => ({
    id: index,
    tender: formatStatusLabel(
      tender.tenderName || tender.tenderType || tender.paymentMethod || "Tender",
    ),
    expected: formatSignedCurrency(
      tender.expectedAmount ?? tender.expected ?? tender.amount,
      currencyCode,
    ),
    actual: formatSignedCurrency(
      tender.actualAmount ?? tender.actual ?? 0,
      currencyCode,
    ),
    variance: formatSignedCurrency(
      tender.varianceAmount ?? tender.variance ?? 0,
      currencyCode,
    ),
  }));

  const reconciliationRows = reconciliationLines.map((line, index) => ({
    id: index,
    item: formatStatusLabel(line.label || line.name || line.type || "Reconciliation"),
    expected: formatSignedCurrency(line.expectedAmount ?? line.expected, currencyCode),
    actual: formatSignedCurrency(line.actualAmount ?? line.actual, currencyCode),
    variance: formatSignedCurrency(line.varianceAmount ?? line.variance, currencyCode),
  }));

  const movementColumns = [
    { key: "type", label: "Type" },
    { key: "amount", label: "Amount" },
    { key: "reason", label: "Reason" },
    { key: "createdAt", label: "Created At" },
  ];
  const comparisonColumns = [
    { key: "tender", label: "Tender" },
    { key: "expected", label: "Expected" },
    { key: "actual", label: "Actual" },
    { key: "variance", label: "Variance" },
  ];

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Cash Session</h1>
          <p className="text-sm text-slate-600">
            Session activity, cash totals, and drawer reconciliation.
          </p>
        </div>
        <Button
          size="sm"
          variant="secondary"
          onClick={() =>
            navigate({
              pathname: "/reporting/cashing-up/sessions",
              search: location.search,
            })
          }
        >
          Back to Sessions
        </Button>
      </div>

      {!storeId ? (
        <Alert tone="warning">The store is required to load this session.</Alert>
      ) : isLoading || isFetching ? (
        <Card className="p-5 sm:p-6">
          <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
            {[1, 2, 3, 4].map((value) => (
              <div key={value} className="h-20 animate-pulse rounded-lg bg-slate-100" />
            ))}
          </div>
        </Card>
      ) : isError ? (
        <Alert tone="error">
          {error?.data?.detail || "Failed to load the cash session detail."}
        </Alert>
      ) : !session ? (
        <Alert tone="warning">This session could not be found.</Alert>
      ) : (
        <div className="space-y-5">
          <Card className="p-5 sm:p-6">
            <Section title="Session Overview">
              <div className="grid gap-x-8 sm:grid-cols-2 xl:grid-cols-4">
                {overviewRows.map((row) => (
                  <DetailPair
                    key={row.label}
                    label={row.label}
                    value={row.value}
                    emphasize={row.label === "Session Status"}
                  />
                ))}
              </div>
            </Section>
          </Card>

          <Card className="p-5 sm:p-6">
            <Section title="Financial Summary">
              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <Metric
                  label="Total Sales"
                  value={formatSignedCurrency(session.totalSales, currencyCode)}
                />
                <Metric
                  label="PDQ Sales"
                  value={formatSignedCurrency(session.cardSales, currencyCode)}
                />
                <Metric
                  label="Expected Cash"
                  value={formatSignedCurrency(session.expectedCash, currencyCode)}
                />
                <Metric
                  label="Counted Cash"
                  value={
                    session.actualCash == null
                      ? "Not counted"
                      : formatSignedCurrency(session.actualCash, currencyCode)
                  }
                />
                <Metric
                  label="Variance"
                  value={session.cashVariance == null ? "Pending" : variance.value}
                  tone={variance.tone}
                />
              </div>
            </Section>
          </Card>

          {breakdownRows.length > 0 && (
            <Card className="p-5 sm:p-6">
              <Section
                title="Expected Cash Breakdown"
                subtitle="Cash activity used by the system to calculate the expected drawer total."
              >
                <div className="grid gap-x-8 gap-y-4 sm:grid-cols-2 xl:grid-cols-4">
                  {breakdownRows.map(([label, value]) => (
                    <DetailPair
                      key={label}
                      label={label}
                      value={formatSignedCurrency(value, currencyCode)}
                    />
                  ))}
                </div>
              </Section>
            </Card>
          )}

          <Card className="p-5 sm:p-6">
            <Section
              title="Cash Movements"
            >
              <Table
                columns={movementColumns}
                rows={movementRows}
                emptyText="No cash movements were recorded for this session."
                headCellClassName="pr-5"
                bodyCellClassName="pr-5"
              />
            </Section>
          </Card>

          {tenderRows.length > 0 && (
            <Card className="p-5 sm:p-6">
              <Section title="Tender Summary">
                <Table
                  columns={comparisonColumns}
                  rows={tenderRows}
                  headCellClassName="pr-5"
                  bodyCellClassName="pr-5"
                />
              </Section>
            </Card>
          )}

          {reconciliationRows.length > 0 && (
            <Card className="p-5 sm:p-6">
              <Section title="Reconciliation Details">
                <Table
                  columns={comparisonColumns.map((column) =>
                    column.key === "tender" ? { ...column, key: "item", label: "Item" } : column,
                  )}
                  rows={reconciliationRows}
                  headCellClassName="pr-5"
                  bodyCellClassName="pr-5"
                />
              </Section>
            </Card>
          )}

        </div>
      )}
    </div>
  );
};

export default CashSessionDetail;
